import { makeAutoObservable } from 'mobx'
import type { IIconProps } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ISmartCallFlowSendToVoicemailStep } from 'entities/SmartCall/api/types'
import type { Inbox } from 'entities/Inbox/model/Inbox'
import { FlowActionStore } from 'widgets/Flow'
import {
  generateSmartCallActionId,
  type ISmartCallActionStore,
  type ISmartCallFlowActionStoreProps,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/Action'
import { GreetingAudioBlockStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/blocks/GreetingAudioBlock'
import { type ISmartCallFlowActionType } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'

export type ISendToVoicemailStoreProps = {
  id?: ISmartCallFlowActionStoreProps['id']
  parentId: ISmartCallFlowActionStoreProps['parentId']
  args?: ISmartCallFlowSendToVoicemailStep['args']
}

export class SendToVoicemailStore {
  static readonly type = 'sendToVoicemail' satisfies ISmartCallFlowActionType
  static title = 'Send to voicemail'
  static iconProps: IIconProps = {
    icon: 'voicemail',
    color: 'var(--action-contained-primary-normal)',
  }
  public readonly actionStore: ISmartCallActionStore

  readonly greetingAudioBlockStore: GreetingAudioBlockStore

  constructor(
    private _entities: IEntitiesStore,
    { id, args, ...props }: ISendToVoicemailStoreProps,
    private parent: { inbox: Inbox | null }
  ) {
    this.actionStore = new FlowActionStore({
      ...props,
      id: id || generateSmartCallActionId(`${this._stepIdType.type}_${this._stepIdType.id}`),
      type: SendToVoicemailStore.type,
      name: SendToVoicemailStore.title,
      allowedNextActions: ['sendSMS', 'aiTextingAgent'],
    })
    this.greetingAudioBlockStore = new GreetingAudioBlockStore(
      this._entities,
      this.parent,
      'Add a voicemail audio message to continue',
      args
    )
    makeAutoObservable(this)
  }

  private _stepIdType: Pick<ISmartCallFlowSendToVoicemailStep, 'id' | 'type'> = {
    id: SendToVoicemailStore.type,
    type: 'action',
  }

  get step(): ISmartCallFlowSendToVoicemailStep {
    return {
      ...this._stepIdType,
      args: {
        ...this.greetingAudioBlockStore.args,
        isGetVoicemailViaEmail: this.isGetVoicemailViaEmail,
      },
      next: this.actionStore.nextId,
    }
  }

  setApiError = (key: string, message: string) => {
    if (key.includes('audioUrl')) {
      this.greetingAudioBlockStore.setError(message)
    }
  }

  validate = () => this.greetingAudioBlockStore.validate()

  get isConfigurationError() {
    return !!this.greetingAudioBlockStore.error
  }

  private _isGetVoicemailViaEmail = false
  get isGetVoicemailViaEmail() {
    return this._isGetVoicemailViaEmail
  }
  setGetVoicemailViaEmail = (value: boolean) => {
    this._isGetVoicemailViaEmail = value
  }
}
